import { createTheme } from "@mui/material";

const fontSize = {
  24: "24px",
  22: "22px",
  20: "20px",
  18: "18px",
  16: "16px",
  14: "14px",
  12: "12px",
  11: "11px",
  10: "10px",
  8: "8px",
};

function generateSizeValues(maxSize, step = 1, unit = "px") {
  const size = {};
  for (let i = 0; i <= maxSize; i += step) {
    size[i] = `${i}${unit}`;
  }
  return size;
}
const size = generateSizeValues(800, 0.5, "px");
const sizeRem = generateSizeValues(3, 0.5, "rem");

const fontWeight = {
  100: 100,
  200: 200,
  300: 300,
  400: 400,
  450: 450,
  500: 500,
  550: 550,
  600: 600,
  700: 700,
  800: 800,
  900: 900,
};

const margin = {
  1: "10px 0",
  2: "40px 0",
  3: "0px 0px 0px 5px",
  4: "0px 25px",
  5: "0 10px",
};

const MJColors = {
  mjnBlue: {
    100: "#1D397E",
    75: "#556a9f",
    50: "#8e9cbf",
    25: "#c6cddf",
    20: "#d9dfee",
    10: "#e9eef7",
    5: "#F3F5F8", // New
    // 15: "#EDF1F8", // New
  },
  midBlue: {
    100: "#00c0f3",
    75: "#3fd0f6",
    50: "#80e0f9",
    25: "#bfeffc",
    20: "#d5f7ff",
    10: "#e5f7ff",
  },
  cyanBlue: {
    100: "#008AAF", // New
    40: "#008AAF4D",
    10: "#008AAF14",
  },
  grey: {
    100: "#565454",
    75: "#817f7f",
    60: "#999999", // New
    50: "#abaaaa",
    25: "#d5d4d4",
  },
  white: {
    100: "#ffffff",
    75: "#f7f7f7",
    50: "#efefef",
    30: "#E8EBF2", // New
    25: "#e7e7e7",
  },
  black: {
    100: "#000000",
    85: "#262B43E5", // New
    80: "#333333", // New
    75: "#3c3c3c",
    60: "#666666", // New
    50: "#787878",
    25: "#b4b4b4",
  },
};

const extra_color = {
  green: "#119B38",
  yellow: "#EEC844",
  lightYellow: "#FFF5E7",
  lightYellow2: "#FFE0AC",
  lightYellow3: "#FE822787",
  red: "#DA0000",
  orange: "#FE8225",
  orangeLight: "#EC8A2C",
};

// * Define util func

function hexToRGBA(hex = MJColors.white[100], opacity = 1) {
  return (
    "rgba(" +
    (hex = hex.replace("#", ""))
      .match(new RegExp("(.{" + hex.length / 3 + "})", "g"))
      .map(function (l) {
        return parseInt(hex.length % 2 ? l + l : l, 16);
      })
      .concat(isFinite(opacity) ? opacity : 1)
      .join(",") +
    ")"
  );
}

export const lightTheme = createTheme({
  palette: {
    hexToRGBA,
    type: "light",
    primary: {
      main: MJColors.mjnBlue[75],
      darkViolet: MJColors.mjnBlue[100],
      lightViolet: MJColors.mjnBlue[50],
      backgroundColor: MJColors.white[100],
      mainLight: MJColors.white[75],
      lightBackground: MJColors.mjnBlue[10],
      lightBlueBackground: MJColors.mjnBlue[10],
      searchBackground: MJColors.white[100],
      searchBackgroundChat: MJColors.white[75],
      searchIcon: MJColors.grey[50],
      loadingContent: MJColors.white[25],
      backgroundLight: MJColors.mjnBlue[5],
      text: MJColors.black[85],
      text2: MJColors.black[80],
      text3: MJColors.black[60],
    },
    secondary: {
      main: MJColors.midBlue[100],
      main2: MJColors.cyanBlue[100],
      mainLight: MJColors.midBlue[25],
      green: MJColors.black[75],
      lightBackground: MJColors.midBlue[25],
    },
    tertiary: {
      black80: MJColors.black[80],
      black: MJColors.black[100],
      light: MJColors.white[100],
      lightGrey: MJColors.white[50],
      lightSmoke: MJColors.white[75],
      lightPurple: MJColors.white[75],
      red: extra_color.red,
      green: extra_color.green,
      yellow: extra_color.yellow,
      darkBlue: MJColors.mjnBlue[100],
      blue: MJColors.midBlue[100],
      grey7: MJColors.black[25],
      lightViolet: MJColors.mjnBlue[25],
      lightVioletBorder: MJColors.mjnBlue[25],
      lightBlue: MJColors.midBlue[10],
      lightMjnBlueBackground: MJColors.mjnBlue[10],
      smartsearchFAQParentBackground: MJColors.white[100],
      smartsearchFAQOutline: MJColors.mjnBlue[25],
      smartsearchFAQHeading: MJColors.mjnBlue[75],
      smartsearchFAQBackground: MJColors.midBlue[20],
      smartsearchFAQText: MJColors.black[75],
      smartsearchBar: MJColors.grey[75],
      smartSearchBarCard: "transparent",
      smartsearchThreadText: MJColors.grey[50],
      smartSearchNewChat: MJColors.mjnBlue[100],
      smartSearchBot: MJColors.white[75],
      smartSearchUser: MJColors.mjnBlue[10],
      divider: MJColors.white[25],
      smartSearchUserText: MJColors.mjnBlue[75],
    },
    shades: {
      backgroundShade1:
        "linear-gradient(97.34deg, #FFFFFF 69.52%, rgba(232, 235, 242, 0.59) 127.29%)",
      hoverBackgrounShade1:
        "linear-gradient(97.34deg, #FFFFFF 69.52%, rgba(139, 202, 219, 0.592005) 127.29%, rgba(0, 138, 175, 0.1) 127.29%)",
    },
    warningColors: {
      red: "#DFF7FD",
      yellow: "#FFF3D9",
      green: "#DBE8DD",
    },
    banner: {
      color: MJColors.white[100],
      backgroundColor: MJColors.mjnBlue[50],
    },
    rapid: {
      lightGrey: MJColors.grey[25],
    },
    stepper: {
      activeBubbleInnerColor: MJColors.midBlue[100],
      activeBubbleOuterColor: MJColors.midBlue[25],
      activeBubbleTextColor: MJColors.white[100],
      activeTextColor: MJColors.midBlue[100],
      activeConnectorColor: MJColors.midBlue[100],
      pendingBubbleTextColor: MJColors.grey[100],
      pendingBubbleColor: MJColors.mjnBlue[20],
      pendingTextColor: MJColors.grey[100],
      pendingConnectorColor: MJColors.black[25],
    },
    rapidInfra: {
      text: MJColors.mjnBlue[100],
      textDisabled: MJColors.mjnBlue[50],
      required: extra_color.red,
      infoIcon: MJColors.mjnBlue[100],
      success: extra_color.green,
      failed: extra_color.red,
      inputBackground: MJColors.white[75],
      inputBackgroundDisabled: MJColors.white[75],
      disabledGrey: MJColors.grey[75],
      disabledGrey2: MJColors.mjnBlue[25],
    },
    rapidDetails: {
      infoIcon: MJColors.black[25],
    },
    kpidashboard: {
      innerPieChartPink1: "#BA0060",
      innerPieChartViolet1: "#4E108D",
      innerPieChartGrey1: "#E6E8ED",
      outerPieChartGrey1: "#E5E4E2",
    },
    grey: {
      0: MJColors.white[75],
      1: MJColors.grey[25],
      3: MJColors.grey[100],
      5: MJColors.grey[25],
      6: MJColors.grey[75],
      8: MJColors.grey[75],
      12: MJColors.mjnBlue[100],
      14: MJColors.grey[50],
      15: MJColors.white[25],
      16: MJColors.grey[50],
      17: MJColors.white[75],
      20: MJColors.grey[25],
      21: MJColors.grey[100],
      22: MJColors.grey[60],
      23: MJColors.white[30],
    },
    text: {
      primary: MJColors.grey[100],
      secondary: MJColors.mjnBlue[100],
      lightGrey: MJColors.grey[100],
      lightToolTip: "rgba(0, 0, 0, 0.87)",
    },
    navbar: {
      main: "#E5F3F7", //MJColors.mjnBlue[75],
      active: MJColors.white[100],
    },
    viewIcon: {
      main: MJColors.black[25],
      active: MJColors.white[100],
    },
    header: { main: MJColors.white[100] },
    searchBar: { main: MJColors.grey[100] },
    cards: {
      background: MJColors.white[75],
      heading: MJColors.mjnBlue[100],
      subheading: MJColors.mjnBlue[100],
      icon: MJColors.midBlue[100],
      hover: MJColors.white[75],
      subTabs: MJColors.mjnBlue[100],
      smallCardButton: MJColors.mjnBlue[25],
      smartSearchAppCardHeading: MJColors.mjnBlue[100],
      smartSearchAppCardSubHeading: MJColors.mjnBlue[100],
    },
    border: { main: MJColors.black[25] },
    table: {
      background: MJColors.mjnBlue[20],
      heading: MJColors.mjnBlue[100],
      subheading: MJColors.mjnBlue[100],
      rowBgColor: MJColors.white[50],
      rowHoverColor: MJColors.white[100],
      accessLevelBg: MJColors.mjnBlue[25],
      rowBgColorOdd: MJColors.white[75],
    },
    lineage: {
      light: MJColors.white[75],
      dark: MJColors.mjnBlue[75],
      whiteText: MJColors.white[100],
      darkText: MJColors.mjnBlue[75],
      background: MJColors.white[75],
      edges: "#626262",
      GOLD: "#E59521",
      SILVER: "#7187A0",
      BRONZE: "#A87841",
      PLATINUM: "#1566A1",
      SOURCE: "#6C8B8C",
    },
    button: {
      background: MJColors.mjnBlue[100],
      text: MJColors.white[100],
    },
    saveAsDraftButton: {
      background: extra_color.orangeLight,
      text: MJColors.white[100],
    },
    form: {
      fieldBackground: MJColors.white[75],
      text: MJColors.mjnBlue[100],
      placeholder: MJColors.mjnBlue[50],
    },
    tabs: {
      background: MJColors.mjnBlue[75],
      text: MJColors.mjnBlue[100],
    },
    faq: {
      background: MJColors.white[75],
      text: MJColors.mjnBlue[75],
      answer: MJColors.grey[100],
    },
    optionsCard: {
      text: MJColors.mjnBlue[100],
      hoverText: MJColors.white[100],
      background: MJColors.white[100],
      hoverBackground: MJColors.midBlue[100],
      border: MJColors.mjnBlue[100],
      hybridCardBackground: "#E0F0EA",
    },
    signupButton: {
      text: MJColors.midBlue[100],
      hoverText: MJColors.white[100],
      background: MJColors.midBlue[100],
      border: MJColors.midBlue[100],
      outlinedText: MJColors.white[100],
      outlinedBackground: "transparent",
    },
    icons: { main: MJColors.black[100] },
    // bg: { main: "linear-gradient(#8d5ac2, #6b16c2)" },
    asterik: { main: extra_color.red },
    graph: {
      background: MJColors.white[75],
      stroke: MJColors.mjnBlue[75],
      text: MJColors.black[75],
      lineColor: MJColors.grey[25],
      headerBar: MJColors.white[50],
      shadow: MJColors.white[50],
    },
    appStatusBadge: {
      fill: MJColors.white[100],
    },
    filterButton: {
      activeColor: MJColors.midBlue[100],
      color: MJColors.mjnBlue[100],
    },

    brain: {
      yellow: {
        text: "#D97706",
        border: "#F59E0B4D",
        borderLight: "#FF98004D",
        background: "#F59E0B1F",
        backgroundLight: "#FF98000A",
      },
      ageColor: {
        l0: "#E3F2FD",
        l1: "#FDE8B5",
        l2: "#FFD180",
        l3: "#FFB74D",
        l4: "#F27841",
        l5: "#EF4444",
      },
      blue: {
        text: "#6366f1",
        background: "#6366F11A",
        border: "#6366F14D",
      },
      lightBlue: {
        text: "#0EA5E9",
        background: "#0EA5E91F",
        border: "#0EA5E94D",
      },
      grey: {
        text: "#94A3B8",
        border: "#64748B40",
        background: "##64748B0F",
        disabledBackground: "#E5E7EB",
        text2: "#8C96A3",
      },
      green: {
        text: "#22C55E",
        border: "#22C55E4D",
        background: "#22C55E0F",
        text2: "#16a34a",
        text3: "#15803d",
      },
      red: {
        text: "#EF4444",
        border: "#EF44444D",
        background: "#EF444412",
      },
      rejected: {
        primaryText: "#9f1239",
        secondaryText: "#7f1d1d",
        background: "#F43F5E0F",
        border: "#F43F5E40",
        secondaryBorder: "#f43f5e",
      },
      cyanBlue: {
        background: MJColors.cyanBlue[10],
        border: MJColors.cyanBlue[40],
      },
      mis: {
        grey: {
          border1: "#00000014",
          border2: "#0000000F",
          border3: MJColors.white[75],
          background1: "#0000001F",
          disabledBackground: "#FFFFFF80",
        },
      },
      table: {
        header: {
          text: "#475569",
          background: "#ECF0F5",
          restrictedText: "#94a3b8",
          restrictedBackground: "#e7edf5",
          restrictedborder: "#cbd5e1",
        },
        row: {
          text: "#64748b",
          background: "#F1F5F9",
          hoverBackground: "#6366F10A",
          editedBackground: "#F59E0B1F",
          editedBorder: "#f59e0b",
          editedCell: "#F59E0B1F",
        },
        tableBackground: "#fff",
        background1: "#F1F5F980",
        stickyColumnBackground: "#FFF1F2",
      },
      drawer: {
        background: "#f8fafc",
        dnd_background: "#f1f5f9",
        dnd_border: "#93c5fd",
        item_background: "#ffffff",
        item_border: "#e2e8f0",
        item_hover_background: "#f8fafc",
        item_hover_border: "#93c5fd",
        text_primary: "#0f172a",
        // text_secondary: "#475569",
        // text_disabled: "#94a3b8",
      },
      sideNav: {
        background: "#fff",
        border: "#E6E7EA",
        boxShadow: "4px 0 12px rgba(0, 0, 0, 0.08)",
        iconColor: "#4B45E6",
        iconBackground: "#E7E6FD",
        hoverColor: "#F3F3FF80",
      },
      error: {
        main: "#EF4444",
      },
    },

    harbor: {
      confidence: {
        high: "#21c45d",
        low: "#f49725",
      },
      confidenceBg: {
        high: "#e9fbf0",
        low: "#fef3e7",
      },
      yellow: {
        text: "#D97706",
        border: "#F59E0B4D",
        borderLight: "#FF98004D",
        background: "#F59E0B1F",
        backgroundLight: "#FF98000A",
      },
      ageColor: {
        l0: "#E3F2FD",
        l1: "#FDE8B5",
        l2: "#FFD180",
        l3: "#FFB74D",
        l4: "#F27841",
        l5: "#EF4444",
      },
      blue: {
        text: "#6366f1",
        background: "#6366F11A",
        border: "#6366F14D",
      },
      lightBlue: {
        text: "#0EA5E9",
        background: "#0EA5E91F",
        border: "#0EA5E94D",
      },
      grey: {
        text: "#94A3B8",
        border: "#64748B40",
        background: "##64748B0F",
        disabledBackground: "#E5E7EB",
        text2: "#8C96A3",
      },
      green: {
        text: "#22C55E",
        border: "#22C55E4D",
        background: "#22C55E0F",
        text2: "#16a34a",
        text3: "#15803d",
      },
      red: {
        text: "#EF4444",
        border: "#EF44444D",
        background: "#EF444412",
      },
      rejected: {
        primaryText: "#9f1239",
        secondaryText: "#7f1d1d",
        background: "#F43F5E0F",
        border: "#F43F5E40",
        secondaryBorder: "#f43f5e",
      },
      cyanBlue: {
        background: MJColors.cyanBlue[10],
        border: MJColors.cyanBlue[40],
      },
      mis: {
        grey: {
          border1: "#00000014",
          border2: "#0000000F",
          border3: MJColors.white[75],
          background1: "#0000001F",
          disabledBackground: "#FFFFFF80",
        },
      },
      table: {
        header: {
          text: "#475569",
          background: "#ECF0F5",
          restrictedText: "#94a3b8",
          restrictedBackground: "#e7edf5",
          restrictedborder: "#cbd5e1",
        },
        row: {
          text: "#64748b",
          background: "#F1F5F9",
          hoverBackground: "#6366F10A",
          editedBackground: "#F59E0B1F",
          editedBorder: "#f59e0b",
          editedCell: "#F59E0B1F",
        },
        tableBackground: "#fff",
        background1: "#F1F5F980",
        stickyColumnBackground: "#FFF1F2",
      },
      drawer: {
        background: "#f8fafc",
        dnd_background: "#f1f5f9",
        dnd_border: "#93c5fd",
        item_background: "#ffffff",
        item_border: "#e2e8f0",
        item_hover_background: "#f8fafc",
        item_hover_border: "#93c5fd",
        text_primary: "#0f172a",
        // text_secondary: "#475569",
        // text_disabled: "#94a3b8",
      },
      sideNav: {
        background: "#fff",
        border: "#E6E7EA",
        boxShadow: "4px 0 12px rgba(0, 0, 0, 0.08)",
        iconColor: "#4B45E6",
        iconBackground: "#E7E6FD",
        hoverColor: "#F3F3FF80",
      },
      error: {
        main: "#EF4444",
      },
    },
  },
  typography: {
    fontFamily: "Noto Sans",
    h1: { fontWeight: 700, fontSize: "1.5rem", lineHeight: 1.3 },
    h2: { fontWeight: 600, fontSize: "1.125rem", lineHeight: 1.4 },
    body2: { fontSize: "0.875rem", color: "hsl(220, 9%, 46%)" },
  },
  fontSize: fontSize,
  //boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.08)",
  boxShadow: "none",
  boxShadow2: "0px 4px 12px 0px #00C0F333",
  border: { 1: `1px solid ${MJColors.mjnBlue[100]}` },
  margin: margin,
  padding: { 1: "10px 15px" },
  fontWeight: fontWeight,
  size: size,
  sizeRem: sizeRem,

  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
    MuiList: {
      dense: true,
    },
    MuiMenuItem: {
      dense: true,
    },
    MuiTable: {
      size: "small",
    },
    MuiTooltip: {
      arrow: false,
    },
  },
  shape: {
    // border: "1px solid rgba(70, 90, 105, 0.5)",
    borderRadius2: 2,
    borderRadius4: 4,
  },
  spacing: 8,
  transitions: { duration: { blazingFast: 100 } },
  custom: {
    layouts: {
      mainScreen: {
        advertisementWrapper: { height: "2rem" },
        headerWrapper: { height: "3.75rem" },
        footerWrapper: { height: "1.25rem" },
      },
    },
    components: {
      sidenav: { width: 220, iconBarWidth: 48, iconBarHeight: 36 },
      row: { height: "3.75rem" },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    hexToRGBA,
    type: "dark",
    primary: {
      main: MJColors.white[100],
      darkViolet: MJColors.white[100],
      lightViolet: MJColors.mjnBlue[50],
      backgroundColor: MJColors.black[100],
      mainLight: MJColors.white[75],
      lightBackground: MJColors.black[100],
      lightBlueBackground: MJColors.black[50],
      searchBackground: MJColors.black[80],
      searchBackgroundChat: MJColors.black[75],
      smartSearchBarCard: MJColors.black[75],
      searchIcon: MJColors.white[100],
      backgroundLight: MJColors.black[75],
      text: MJColors.white[100],
      text2: MJColors.white[100],
      text3: MJColors.white[25],
    },
    secondary: {
      main: extra_color.orange,
      main2: extra_color.orange,
      green: MJColors.black[75],
      lightBackground: MJColors.black[75],
    },
    tertiary: {
      black80: MJColors.white[100],
      black: MJColors.black[100],
      light: MJColors.black[100],
      lightGrey: MJColors.white[50],
      lightSmoke: MJColors.black[75],
      lightPurple: MJColors.white[75],
      lightBlue: MJColors.grey[100],
      lightMjnBlueBackground: MJColors.grey[100],
      darkBlue: extra_color.orange,
      red: extra_color.red,
      green: extra_color.green,
      yellow: extra_color.yellow,
      blue: MJColors.midBlue[100],
      grey7: MJColors.black[25],
      lightViolet: MJColors.mjnBlue[25],
      lightVioletBorder: MJColors.white[100],
      smartsearchFAQParentBackground: extra_color.lightYellow,
      smartsearchFAQOutline: extra_color.lightYellow2,
      smartsearchFAQHeading: extra_color.orange,
      smartsearchFAQBackground: extra_color.lightYellow2,
      smartsearchFAQText: MJColors.black[75],
      smartsearchBar: MJColors.white[100],
      smartsearchThreadText: extra_color.orange,
      smartSearchNewChat: extra_color.orange,
      smartSearchBot: MJColors.white[75],
      smartSearchUser: extra_color.lightYellow,
      divider: MJColors.white[75],
      smartSearchUserText: extra_color.orange,
    },

    shades: {
      backgroundShade1:
        "linear-gradient(97.34deg, #1E1E1E 69.52%, rgba(50, 53, 63, 0.8) 127.29%)",
      hoverBackgrounShade1:
        "linear-gradient(97.34deg, #2A2A2A 69.52%, rgba(80, 85, 100, 0.8) 127.29%, rgba(0, 138, 175, 0.1) 127.29%)",
    },
    warningColors: {
      red: "#5A1E1E",
      yellow: "#66521E",
      green: "#1E3D28",
    },
    banner: {
      color: MJColors.white[100],
      backgroundColor: extra_color.orange,
    },
    rapid: {
      lightGrey: MJColors.grey[25],
    },
    rapidInfra: {
      text: MJColors.mjnBlue[100],
      textDisabled: MJColors.mjnBlue[50],
      required: extra_color.red,
      infoIcon: MJColors.mjnBlue[100],
      success: extra_color.green,
      failed: extra_color.red,
      inputBackground: MJColors.white[75],
      inputBackgroundDisabled: MJColors.white[75],
      disabledGrey: MJColors.grey[75],
      disabledGrey2: MJColors.mjnBlue[25],
    },
    stepper: {
      activeBubbleInnerColor: extra_color.orange,
      activeBubbleOuterColor: extra_color.lightYellow3,
      activeBubbleTextColor: MJColors.white[100],
      activeTextColor: extra_color.orange,
      activeConnectorColor: extra_color.orange,
      pendingBubbleTextColor: MJColors.grey[100],
      pendingBubbleColor: MJColors.black[25],
      pendingTextColor: MJColors.grey[25],
      pendingConnectorColor: MJColors.black[25],
    },
    kpidashboard: {
      innerPieChartPink1: "#BA0060",
      innerPieChartViolet1: "#4E108D",
      innerPieChartGrey1: "#E6E8ED",
      outerPieChartGrey1: "#E5E4E2",
    },
    grey: {
      0: MJColors.white[75],
      1: MJColors.grey[25],
      3: MJColors.grey[100],
      5: MJColors.grey[25],
      6: MJColors.grey[75],
      8: MJColors.grey[75],
      12: MJColors.black[25],
      17: MJColors.white[75],
      20: MJColors.grey[25],
      21: MJColors.grey[100],
      22: MJColors.grey[60],
      23: MJColors.black[75],
    },
    text: {
      primary: MJColors.grey[100],
      secondary: "#fff",
      lightGrey: MJColors.grey[100],
      lightToolTip: "rgba(0, 0, 0, 0.87)",
    },
    navbar: {
      main: MJColors.black[75],
      active: extra_color.orange,
    },
    header: { main: MJColors.black[75] },
    searchBar: { main: MJColors.white[100] },
    cards: {
      background: MJColors.black[75],
      heading: MJColors.white[100],
      subheading: MJColors.black[25],
      icon: extra_color.orange,
      hover: MJColors.black[75],
      subTabs: extra_color.orange,
      smallCardButton: MJColors.grey[100],
      smartSearchAppCardHeading: MJColors.mjnBlue[100],
      smartSearchAppCardSubHeading: MJColors.mjnBlue[100],
    },
    table: {
      background: MJColors.black[75],
      heading: MJColors.white[100],
      subheading: MJColors.black[25],
      rowBgColor: "transparent",
      rowHoverColor: "",
      accessLevelBg: MJColors.grey[100],
      rowBgColorOdd: "transparent",
    },
    viewIcon: {
      main: MJColors.black[25],
      active: MJColors.white[100],
    },
    lineage: {
      light: MJColors.black[75],
      dark: MJColors.black[50],
      whiteText: MJColors.white[100],
      darkText: MJColors.white[100],
      background: MJColors.black[100],
      GOLD: "#E59521",
      SILVER: "#7187A0",
      BRONZE: "#A87841",
      PLATINUM: "#1566A1",
      SOURCE: "#6C8B8C",
    },
    tabs: {
      background: extra_color.orange,
      text: MJColors.white[100],
    },
    button: {
      background: extra_color.orange,
      text: MJColors.white[100],
    },
    saveAsDraftButton: {
      background: extra_color.orangeLight,
      text: MJColors.white[100],
    },
    form: {
      fieldBackground: MJColors.black[75],
      text: MJColors.white[100],
      placeholder: MJColors.grey[75],
    },
    border: { main: MJColors.black[25] },
    faq: {
      background: MJColors.black[75],
      text: MJColors.white[100],
      answer: MJColors.white[100],
    },
    optionsCard: {
      text: MJColors.white[100],
      hoverText: MJColors.white[100],
      background: MJColors.black[75],
      hoverBackground: extra_color.orange,
      border: MJColors.mjnBlue[100],
      hybridCardBackground: "#2E4D43",
    },
    signupButton: {
      text: extra_color.orange,
      hoverText: MJColors.white[100],
      background: extra_color.orange,
      border: extra_color.orange,
      outlinedText: MJColors.white[100],
      outlinedBackground: "transparent",
    },
    icons: { main: MJColors.white[100] },
    // bg: { main: "linear-gradient(#8d5ac2, #6b16c2)" },
    asterik: { main: extra_color.red },
    graph: {
      background: MJColors.black[75],
      stroke: extra_color.orange,
      text: MJColors.white[100],
      lineColor: MJColors.grey[25],
      headerBar: MJColors.black[75],
      shadow: MJColors.black[75],
    },
    appStatusBadge: {
      fill: MJColors.white[100],
    },
    filterButton: {
      activeColor: extra_color.orange,
      color: MJColors.white[100],
    },
    brain: {
      yellow: {
        text: "#FBBF24",
        border: "#F59E0B66",
        background: "#F59E0B29",
        backgroundLight: "#FF980033",
      },
      ageColor: {
        l0: "#90CAF9",
        l1: "#FFE082",
        l2: "#FFCC80",
        l3: "#FFA726",
        l4: "#FF7043",
        l5: "#EF5350",
      },
      blue: {
        text: "#818CF8",
        background: "#6366F129",
        border: "#6366F14D",
      },
      lightBlue: {
        text: "#38BDF8",
        background: "#38BDF814",
        border: "#38BDF833",
      },

      grey: {
        text: "#CBD5F1",
        border: "#64748B66",
        background: "#64748B1A",
        disabledBackground: "#1A1D21",
      },

      green: {
        text: "#4ADE80",
        border: "#22C55E66",
        background: "#22C55E1A",
        text2: "#22C55E",
        text3: "#16A34A",
      },

      red: {
        text: "#F87171",
        border: "#EF444466",
        background: "#EF44441A",
      },
      rejected: {
        primaryText: "#FDA4AF",
        secondaryText: "#FECDD3",
        background: "#F43F5E1A",
        border: "#FB718580",
        secondaryBorder: "#FB7185",
      },

      cyanBlue: {
        background: "#1E1E1E",
        border: "#1E1E1E",
      },

      mis: {
        grey: {
          border1: "#FFFFFF14",
          border2: "#FFFFFF0F",
          border3: "none",
          background1: "#FFFFFF1F",
          disabledBackground: "#00000080",
        },
      },

      table: {
        header: {
          text: "#CBD5F1",
          background: "#1E293B",
          restrictedText: "#94a3b8",
          restrictedBackground: "#1f2937",
          restrictedBorder: "#374151",
        },

        row: {
          text: "#fff",
          background: "#0F172A",
          hoverBackground: "#6366F114",
          editedBackground: "#F59E0B29",
          editedBorder: "#F59E0B",
          editedCell: "#F59E0B29",
        },
        tableBackground: "#1E1E1E",
        background1: "#64748B1A",
        stickyColumnBackground: "#1F1115",
      },

      drawer: {
        background: "#111827",
        dnd_background: "#1f2937",
        dnd_border: "#60a5fa",
        item_background: "#374151",
        item_border: "#4b5563",
        item_hover_background: "#4b5563",
        item_hover_border: "#60a5fa",
        text_primary: "#f9fafb",
        // text_secondary: "#d1d5db",
        // text_disabled: "#9ca3af",
      },

      sideNav: {
        background: "#1F1F1F",
        border: "#2A2A2A",
        boxShadow: "6px 0 18px rgba(0, 0, 0, 0.75)",
        iconColor: extra_color.orange,
        iconBackground: "#FFF1E6",
        hoverColor: "#FFFFFF0F",
      },

      error: {
        main: "#F87171",
      },
    },

    harbor: {
      confidence: {
        high: "#21c45d",
        low: "#f49725",
      },
      confidenceBg: {
        high: "#005220",
        low: "#783e00",
      },
      yellow: {
        text: "#FBBF24",
        border: "#F59E0B66",
        background: "#F59E0B29",
        backgroundLight: "#FF980033",
      },
      ageColor: {
        l0: "#90CAF9",
        l1: "#FFE082",
        l2: "#FFCC80",
        l3: "#FFA726",
        l4: "#FF7043",
        l5: "#EF5350",
      },
      blue: {
        text: "#818CF8",
        background: "#6366F129",
        border: "#6366F14D",
      },
      lightBlue: {
        text: "#38BDF8",
        background: "#38BDF814",
        border: "#38BDF833",
      },

      grey: {
        text: "#CBD5F1",
        border: "#64748B66",
        background: "#64748B1A",
        disabledBackground: "#1A1D21",
      },

      green: {
        text: "#4ADE80",
        border: "#22C55E66",
        background: "#22C55E1A",
        text2: "#22C55E",
        text3: "#16A34A",
      },

      red: {
        text: "#F87171",
        border: "#EF444466",
        background: "#EF44441A",
      },
      rejected: {
        primaryText: "#FDA4AF",
        secondaryText: "#FECDD3",
        background: "#F43F5E1A",
        border: "#FB718580",
        secondaryBorder: "#FB7185",
      },

      cyanBlue: {
        background: "#1E1E1E",
        border: "#1E1E1E",
      },

      mis: {
        grey: {
          border1: "#FFFFFF14",
          border2: "#FFFFFF0F",
          border3: "none",
          background1: "#FFFFFF1F",
          disabledBackground: "#00000080",
        },
      },

      table: {
        header: {
          text: "#CBD5F1",
          background: "#1E293B",
          restrictedText: "#94a3b8",
          restrictedBackground: "#1f2937",
          restrictedBorder: "#374151",
        },

        row: {
          text: "#fff",
          background: "#0F172A",
          hoverBackground: "#6366F114",
          editedBackground: "#F59E0B29",
          editedBorder: "#F59E0B",
          editedCell: "#F59E0B29",
        },
        tableBackground: "#1E1E1E",
        background1: "#64748B1A",
        stickyColumnBackground: "#1F1115",
      },

      drawer: {
        background: "#111827",
        dnd_background: "#1f2937",
        dnd_border: "#60a5fa",
        item_background: "#374151",
        item_border: "#4b5563",
        item_hover_background: "#4b5563",
        item_hover_border: "#60a5fa",
        text_primary: "#f9fafb",
        // text_secondary: "#d1d5db",
        // text_disabled: "#9ca3af",
      },

      sideNav: {
        background: "#1F1F1F",
        border: "#2A2A2A",
        boxShadow: "6px 0 18px rgba(0, 0, 0, 0.75)",
        iconColor: extra_color.orange,
        iconBackground: "#FFF1E6",
        hoverColor: "#FFFFFF0F",
      },

      error: {
        main: "#F87171",
      },
    },
  },
  typography: {
    fontFamily: "Noto Sans",
    h1: { fontWeight: 700, fontSize: "1.5rem", lineHeight: 1.3 },
    h2: { fontWeight: 600, fontSize: "1.125rem", lineHeight: 1.4 },
    body2: { fontSize: "0.875rem", color: "hsl(220, 9%, 46%)" },
  },
  fontSize: fontSize,
  boxShadow: "none",
  border: { 1: `1px solid ${MJColors.grey[100]}` },
  margin: margin,
  padding: { 1: "10px 15px" },
  fontWeight: fontWeight,
  size: size,
  sizeRem: sizeRem,

  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
    MuiList: {
      dense: true,
    },
    MuiMenuItem: {
      dense: true,
    },
    MuiTable: {
      size: "small",
    },
    MuiTooltip: {
      arrow: false,
    },
  },
  shape: {
    // border: "1px solid rgba(70, 90, 105, 0.5)",
    borderRadius2: 2,
    borderRadius4: 4,
  },
  spacing: 8,
  transitions: { duration: { blazingFast: 100 } },
  custom: {
    layouts: {
      mainScreen: {
        headerWrapper: { height: "3.75rem" },
        footerWrapper: { height: "1.25rem" },
      },
    },
    components: {
      sidenav: { width: 220, iconBarWidth: 48, iconBarHeight: 36 },
      row: { height: "3.75rem" },
    },
  },
});
